import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { readErr } from '@/lib/api';
import { AuthFrame } from '@/pages/Login';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/lib/toast';

export default function Verify() {
    const navigate = useNavigate();
    const location = useLocation();
    const { verify, resendCode, loading } = useAuth();

    // Email is passed via location.state from Signup; fallback to ?email=.
    const params = new URLSearchParams(location.search);
    const initialEmail = location.state?.email || params.get('email') || '';
    const [email, setEmail] = useState(initialEmail);
    const [code, setCode] = useState('');
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [resendCooldown]);

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            const u = await verify(email, code);
            toast.success(`Welcome aboard, ${u.name}.`);
            navigate('/app/ask');
        } catch (err) {
            toast.error(readErr(err, 'Verification failed'));
        }
    };

    const onResend = async () => {
        if (resendCooldown > 0) return;
        try {
            await resendCode(email);
            toast.success('A fresh code is on its way to your inbox.');
            setResendCooldown(30);
        } catch (err) {
            toast.error(readErr(err, 'Could not resend'));
        }
    };

    return (
        <AuthFrame
            title="Verify your email"
            subtitle="We sent a 6-digit code to your inbox. Valid for 15 minutes."
        >
            <form onSubmit={onSubmit} className="space-y-5" data-testid="verify-form">
                <div>
                    <Label className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
                    <Input
                        type="email" required value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11"
                        data-testid="verify-email"
                    />
                </div>
                <div>
                    <Label className="text-slate-300 text-xs uppercase tracking-wider">Verification code</Label>
                    <Input
                        required value={code} maxLength={6} autoFocus
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                        className="mt-2 bg-transparent border-b border-marigold/20 border-t-0 border-l-0 border-r-0 rounded-none px-0 focus-visible:ring-0 focus-visible:border-marigold text-parchment h-11 font-mono tracking-[0.5em] text-center"
                        data-testid="verify-code"
                    />
                </div>
                <Button
                    type="submit" disabled={loading || code.length < 4}
                    className="w-full bg-marigold text-obsidian hover:bg-marigold-hover rounded-none h-11 font-medium"
                    data-testid="verify-submit"
                >
                    Verify and continue
                </Button>
            </form>
            <div className="mt-6 flex items-center justify-between text-sm text-slate-400">
                <button
                    type="button" onClick={onResend} disabled={resendCooldown > 0}
                    className="text-marigold hover:text-marigold-50 disabled:text-slate-600 disabled:cursor-not-allowed"
                    data-testid="verify-resend"
                >
                    {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
                <Link to="/login" className="text-marigold hover:text-marigold-50">Back to sign in</Link>
            </div>
        </AuthFrame>
    );
}
